/**
 * Map a backend error (Pydantic 422 or FastAPI HTTPException) to per-field react-hook-form errors.
 * Falls back to a single root-level error message if the shape isn't recognized.
 */
import type { UseFormSetError, FieldValues, Path } from 'react-hook-form';

interface PydanticIssue {
  loc?: (string | number)[];
  msg?: string;
  type?: string;
}

interface MaybeError {
  detail?: string | PydanticIssue[];
  message?: string;
}

export function applyServerErrors<T extends FieldValues>(
  err: unknown,
  setError: UseFormSetError<T>,
): void {
  const fallback = 'Something went wrong. Please try again.';

  if (!(err instanceof Error)) {
    setError('root.serverError' as Path<T>, { message: fallback });
    return;
  }

  // Our api client throws Error("<status>: <text>"). The text is the response body.
  const match = err.message.match(/^(\d{3}):\s*(.*)$/s);
  const status = match?.[1] ? Number(match[1]) : undefined;
  const body = match?.[2] ?? err.message;

  let parsed: MaybeError | null = null;
  try {
    parsed = JSON.parse(body) as MaybeError;
  } catch {
    parsed = null;
  }

  // Pydantic 422 shape: { detail: [{loc, msg, type}, ...] }
  if (parsed && Array.isArray(parsed.detail)) {
    for (const issue of parsed.detail) {
      const fieldPath = (issue.loc ?? []).filter((p) => p !== 'body').join('.') || 'root.serverError';
      setError(fieldPath as Path<T>, { message: issue.msg ?? 'Invalid' });
    }
    return;
  }

  // FastAPI HTTPException default: { detail: "code_like_string" }
  if (parsed && typeof parsed.detail === 'string') {
    const map: Record<string, { path?: string; msg: string }> = {
      email_taken: { path: 'email', msg: 'This email is already registered. Try signing in.' },
      invalid_credentials: { msg: 'Email or password is incorrect.' },
      weak_password: { path: 'password', msg: 'Choose a stronger password.' },
      account_disabled: { msg: 'This account is disabled. Contact support.' },
    };
    const known = map[parsed.detail];
    if (known) {
      setError((known.path ?? 'root.serverError') as Path<T>, { message: known.msg });
      return;
    }
    setError('root.serverError' as Path<T>, {
      message: status === 401 ? 'Email or password is incorrect.' : parsed.detail,
    });
    return;
  }

  setError('root.serverError' as Path<T>, { message: fallback });
}
